import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Building2, Mail,
  Lock,
  CheckCircle, ClipboardList, AlertTriangle,
  Smartphone, CheckSquare, Video, Mic, Calendar,
  RefreshCw, Check, PenLine, ChevronUp
} from 'lucide-react';
import { FormRenderResponse, ConsentFormData, TemplateConsent, EvidenceData, EvidenceType } from '../../types/consent';
import SignaturePad from '../SignaturePad';
import ContentRenderer from './ContentRenderer';
import OTPStep from './OTPStep';
import VideoRecordingStep from './VideoRecordingStep';
import AudioRecordingStep from './AudioRecordingStep';
import { Card, Button } from '../../design-system';
import { LanguageSelector } from '../common/LanguageSelector';
import { useI18nSafe } from '../../i18n';
import styles from './SimpleConsentWizard.module.css';

// localStorage persistence configuration
const STORAGE_KEY_PREFIX = 'consent_progress_';
const STORAGE_EXPIRY_HOURS = 24;

interface StoredProgress {
  documentsRead: string[];
  acceptedTerms: boolean;
  currentStep: string;
  timestamp: number;
  expiresAt: number;
}

// Load saved progress from localStorage
const loadProgress = (urlId: string): StoredProgress | null => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${urlId}`);
    if (!stored) return null;
    
    const progress: StoredProgress = JSON.parse(stored);
    if (Date.now() > progress.expiresAt) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${urlId}`);
      return null;
    }
    return progress;
  } catch {
    return null;
  }
};

// Save progress to localStorage
const saveProgress = (urlId: string, progress: Partial<StoredProgress>) => {
  try {
    const existing = loadProgress(urlId) || {
      documentsRead: [],
      acceptedTerms: false,
      currentStep: 'welcome',
      timestamp: Date.now(),
      expiresAt: Date.now() + (STORAGE_EXPIRY_HOURS * 60 * 60 * 1000)
    };
    
    const updated = { ...existing, ...progress, timestamp: Date.now() };
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${urlId}`, JSON.stringify(updated));
  } catch (error) {
    console.warn('Could not save consent progress:', error);
  }
};

// Normalize evidence type from API format (phone-otp) to internal format (PHONE_OTP)
const normalizeEvidenceType = (type: string): EvidenceType => {
  return type.toUpperCase().replace(/-/g, '_') as EvidenceType;
};

// Format phone number for display (mask middle digits)
const formatPhoneNumber = (phone?: string): string => {
  if (!phone) return '';
  if (phone.length <= 4) return phone;
  const last4 = phone.slice(-4);
  const masked = '*'.repeat(Math.max(0, phone.length - 4));
  return masked + last4;
};

// Get friendly evidence type name
const getEvidenceTypeName = (type: EvidenceType): { name: string; icon: React.ReactNode; description: string } => {
  switch (type) {
    case 'SIGNATURE':
      return { name: 'E-Signature based consent', icon: <PenLine size={20} />, description: 'Draw your signature' };
    case 'PHONE_OTP':
    case 'SMS_OTP':
      return { name: 'OTP based consent', icon: <Smartphone size={20} />, description: 'Verify via SMS code' };
    case 'EMAIL_OTP':
      return { name: 'Email OTP based consent', icon: <Mail size={20} />, description: 'Verify via email code' };
    case 'CHECKBOX':
      return { name: 'Checkbox based consent', icon: <CheckSquare size={20} />, description: 'Confirm by checking' };
    case 'VIDEO':
      return { name: 'Video based consent', icon: <Video size={20} />, description: 'Record video consent' };
    case 'AUDIO':
      return { name: 'Audio based consent', icon: <Mic size={20} />, description: 'Record audio consent' };
    default:
      return { name: 'Verification', icon: <Check size={20} />, description: 'Verify your consent' };
  }
};

// Check if consent is expiring soon (within 7 days)
const isExpiringSoon = (expiryTime?: string): boolean => {
  if (!expiryTime) return false;
  const expiryDate = new Date(expiryTime);
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
};

// Check if consent has expired
const isExpired = (expiryTime?: string): boolean => {
  if (!expiryTime) return false;
  return new Date(expiryTime) < new Date();
};

interface SimpleConsentWizardProps {
  formData: FormRenderResponse;
  onComplete: (data: ConsentFormData) => Promise<void>;
  tenantId: string;
  urlId: string;
  token: string;
}

interface DocumentInfo {
  type: 'privacy' | 'terms' | 'consent';
  title: string;
  icon: React.ReactNode;
  content: string;
  contentType?: string;
  templateId: string;
  required: boolean;
}

interface DPOInfo {
  name?: string;
  email?: string;
  phone?: string;
}

interface GrievanceOfficerInfo {
  name?: string;
  email?: string;
  phone?: string;
}

interface OrgInfo {
  name: string;
  logo?: string;
  description?: string;
  role?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  dpo?: DPOInfo;
  grievanceOfficer?: GrievanceOfficerInfo;
}

// Simple 3-step wizard: Welcome → Review Documents → Provide Consent
type SimpleStep = 'welcome' | 'documents' | 'consent' | 'otp' | 'success';

const SimpleConsentWizard: React.FC<SimpleConsentWizardProps> = ({
  formData,
  onComplete,
  tenantId,
  urlId,
  token
}) => {
  const { t } = useI18nSafe();
  const [currentStep, setCurrentStep] = useState<SimpleStep>('welcome');
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Document reading state
  const [documentsRead, setDocumentsRead] = useState<Set<string>>(new Set());
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [scrolledDocs, setScrolledDocs] = useState<Set<string>>(new Set());

  // Consent state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signature, setSignature] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceType | null>(null);
  const [evidenceData, setEvidenceData] = useState<EvidenceData | null>(null);
  const [dataCategoriesOpen, setDataCategoriesOpen] = useState(true);
  const consentExpiry = formData.consentExpiresAt || formData.expiryTime;

  // Keyboard navigation handler for accessibility
  const handleKeyboardNavigation = useCallback((e: KeyboardEvent) => {
    // Escape key to go back
    if (e.key === 'Escape' && currentStep !== 'welcome' && currentStep !== 'success') {
      e.preventDefault();
      if (currentStep === 'documents') {
        setDirection(-1);
        setCurrentStep('welcome');
      } else if (currentStep === 'consent') {
        setDirection(-1);
        setCurrentStep('documents');
      }
    }
  }, [currentStep]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardNavigation);
    return () => document.removeEventListener('keydown', handleKeyboardNavigation);
  }, [handleKeyboardNavigation]);

  // Load saved progress from localStorage on mount
  useEffect(() => {
    const saved = loadProgress(urlId);
    if (saved && saved.currentStep !== 'success') {
      setDocumentsRead(new Set(saved.documentsRead));
      setAcceptedTerms(saved.acceptedTerms);
      // Only restore step if we have valid progress
      if (saved.documentsRead.length > 0 && saved.currentStep !== 'welcome') {
        setCurrentStep(saved.currentStep as SimpleStep);
      }
    }
  }, [urlId]);

  // Save progress whenever state changes
  useEffect(() => {
    if (currentStep !== 'success') {
      saveProgress(urlId, {
        documentsRead: Array.from(documentsRead),
        acceptedTerms,
        currentStep
      });
    }
  }, [documentsRead, acceptedTerms, currentStep, urlId]);

  // Extract all documents from templates
  const allDocuments = useMemo((): DocumentInfo[] => {
    const docs: DocumentInfo[] = [];
    
    formData.templates.forEach(template => {
      if (template.hasPrivacyPolicy && template.privacyPolicyData) {
        docs.push({
          type: 'privacy',
          title: 'Privacy Policy',
          icon: <Lock size={20} />,
          content: template.privacyPolicyData,
          contentType: template.privacyPolicyDataType,
          templateId: template.templateId,
          required: true
        });
      }
      if (template.hasTermOfService && template.termOfServiceData) {
        docs.push({
          type: 'terms',
          title: 'Terms of Service',
          icon: <ClipboardList size={20} />,
          content: template.termOfServiceData,
          contentType: template.termOfServiceDataType,
          templateId: template.templateId,
          required: true
        });
      }
      if (template.hasConsent && template.consentData) {
        docs.push({
          type: 'consent',
          title: 'Consent',
          icon: <PenLine size={20} />,
          content: template.consentData,
          contentType: template.consentDataType,
          templateId: template.templateId,
          required: true
        });
      }
    });
    
    return docs;
  }, [formData.templates]);

  // Get organizations from fiduciaries
  const organizations = useMemo((): OrgInfo[] => {
    const orgs: OrgInfo[] = [];
    const seen = new Set<string>();
    
    // From fiduciaries (includes DPO and Grievance Officer info)
    if (formData.fiduciaries) {
      Object.values(formData.fiduciaries).forEach((fid: any) => {
        if (fid.name && !seen.has(fid.name)) {
          seen.add(fid.name);
          orgs.push({
            name: fid.name,
            logo: fid.logo,
            description: fid.description,
            role: fid.role,
            contactEmail: fid.contactEmail,
            contactPhone: fid.contactPhone,
            website: fid.website,
            dpo: fid.dpo,
            grievanceOfficer: fid.grievanceOfficer
          });
        }
      });
    }
    
    // From templates (fallback for legacy data)
    formData.templates.forEach(t => {
      const name = t.dataCollector?.name;
      if (name && !seen.has(name)) {
        seen.add(name);
        orgs.push({
          name,
          logo: t.dataCollector?.logo,
          description: t.dataCollector?.description
        });
      }
    });
    
    return orgs;
  }, [formData]);

  // Extract all data categories from templates and projects
  const dataCategories = useMemo((): string[] => {
    const categories = new Set<string>();
    
    // From templates
    formData.templates.forEach(template => {
      if (template.dataCategories) {
        template.dataCategories.forEach(cat => categories.add(cat));
      }
    });
    
    // From projects
    if (formData.projects) {
      Object.values(formData.projects).forEach((project: any) => {
        if (project.dataCategories) {
          project.dataCategories.forEach((cat: string) => categories.add(cat));
        }
      });
    }
    
    return Array.from(categories);
  }, [formData]);

  // Available evidence types - normalized
  const evidenceTypes = useMemo(() => {
    const types = formData.evidenceTypes || ['SIGNATURE'];
    return types.map(t => normalizeEvidenceType(t));
  }, [formData.evidenceTypes]);

  // Auto-select evidence type
  useEffect(() => {
    if (!selectedEvidence && evidenceTypes.length > 0) {
      // Prefer signature if available, otherwise first available
      if (evidenceTypes.includes('SIGNATURE')) {
        setSelectedEvidence('SIGNATURE');
      } else {
        setSelectedEvidence(evidenceTypes[0]);
      }
    }
  }, [evidenceTypes, selectedEvidence]);

  const allDocsRead = documentsRead.size >= allDocuments.length;
  
  const canSubmit = acceptedTerms && allDocsRead && (
    selectedEvidence === 'SIGNATURE' ? signature.length > 0 :
    selectedEvidence === 'CHECKBOX' ? true :
    selectedEvidence === 'VIDEO' ? evidenceData?.videoUrl !== undefined :
    selectedEvidence === 'AUDIO' ? evidenceData?.audioUrl !== undefined :
    evidenceData?.otpVerified
  );

  const goToStep = (step: SimpleStep, dir: number = 1) => {
    setDirection(dir);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markDocumentRead = (docId: string) => {
    setDocumentsRead(prev => new Set([...prev, docId]));
  };

  const unmarkDocumentRead = (docId: string) => {
    setDocumentsRead(prev => {
      const newSet = new Set(prev);
      newSet.delete(docId);
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      const templateConsents: TemplateConsent[] = formData.templates.map(t => ({
        templateId: t.templateId,
        consentAccepted: acceptedTerms,
        signature: signature || `${selectedEvidence}_CONFIRMED`,
        evidenceData: evidenceData || { 
          type: selectedEvidence!, 
          signature: selectedEvidence === 'SIGNATURE' ? signature : undefined,
          checkboxAccepted: selectedEvidence === 'CHECKBOX' ? true : undefined
        }
      }));

      const submissionData: ConsentFormData = {
        consentAccepted: acceptedTerms,
        signature: signature || `${selectedEvidence}_CONFIRMED`,
        templates: templateConsents,
        evidenceData: evidenceData || { 
          type: selectedEvidence!,
          signature: selectedEvidence === 'SIGNATURE' ? signature : undefined,
          checkboxAccepted: selectedEvidence === 'CHECKBOX' ? true : undefined
        }
      };

      await onComplete(submissionData);
      goToStep('success');
    } catch (err: any) {
      setError(err.message || 'Failed to submit consent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle OTP completion
  const handleOtpComplete = (otp: string, verified: boolean) => {
    if (verified) {
      setEvidenceData({
        type: selectedEvidence!,
        otpValue: otp,
        otpVerified: true
      });
      // Auto submit after OTP verification
      goToStep('consent');
    }
  };

  // Render Welcome Step - Improved with clearer information
  const renderWelcome = () => (
    <div className={`${styles['simple-wizard-step']} ${styles['welcome-step']}`}>
      <div className={`${styles['welcome-header']} ${styles['compact']}`}>
        <img src="/consent-hero.svg" alt="Your Consent is Required" className={styles['welcome-hero-svg']} />
      </div>

      {/* Data Owner Info - Show who is consenting */}
      {formData.dataOwner && (
        <div className={styles['data-owner-card']}>
          <div className={styles['data-owner-icon']}><User size={20} /></div>
          <div className={styles['data-owner-info']}>
            <span className={styles['data-owner-label']}>{t.consent?.consentingAs || 'Consenting as'}</span>
            <strong className={styles['data-owner-name']}>
              {formData.dataOwner.firstName} {formData.dataOwner.lastName}
            </strong>
            {formData.dataOwner.email && (
              <span className={styles['data-owner-contact']}>{formData.dataOwner.email}</span>
            )}
          </div>
        </div>
      )}

      {/* Consent Expiry - moved to after What You'll Do */}

      {/* Organizations requesting consent */}
      {organizations.length > 0 && (
        <div className={styles['org-section']}>
          <h2>{t.consent?.requestingOrg || 'Requesting Organisation'}</h2>
          <p className={styles['section-subtitle']}>Following Organisation needs your consent to proceed</p>
          <div className={styles['org-list']}>
            {organizations.map((org, idx) => (
              <div key={idx} className={styles['org-card']}>
                {org.logo ? (
                  <img src={org.logo} alt="" className={styles['org-logo']} />
                ) : (
                  <div className={styles['org-logo-placeholder']}><Building2 size={16} /></div>
                )}
                <strong className={styles['org-name']}>{org.name}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Categories Section - What data is being collected */}
      {dataCategories.length > 0 && (
        <div className={styles['data-categories-section']}>
          <button className={styles['section-toggle']} onClick={() => setDataCategoriesOpen(!dataCategoriesOpen)}>
            <h2>{t.consent?.dataBeingCollected || 'Data Being Collected'}</h2>
            <ChevronUp size={16} className={`${styles['section-chevron']} ${dataCategoriesOpen ? '' : styles['collapsed']}`} />
          </button>
          {dataCategoriesOpen && (
            <div className={styles['data-categories-list']}>
              {dataCategories.map((category, idx) => (
                <div key={idx} className={styles['data-category-row']}>
                  <span className={styles['category-name']}>{category}</span>
                  <CheckSquare size={16} color="#ea582c" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* What you'll do - Clear steps preview */}
      <div className={styles['steps-preview']}>
        <h3>{t.consent?.whatYouWillDo || "What you will do"}</h3>
        <div className={styles['steps-list']}>
          <div className={styles['step-preview-item']}>
            <span className={styles['step-number']}>1</span>
            <div className={styles['step-preview-content']}>
              <strong>Review Document</strong>
              <span>Read all required documents.</span>
            </div>
          </div>
          <div className={styles['step-preview-item']}>
            <span className={styles['step-number']}>2</span>
            <div className={styles['step-preview-content']}>
              <strong>Consent Confirmation</strong>
              <span>Choose the mode for consent confirmation.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Consent Expiry - only shown when data is present */}
      {consentExpiry && (
        <div className={`${styles['consent-expiry-card']} ${isExpired(consentExpiry) ? styles['expired'] : isExpiringSoon(consentExpiry) ? styles['expiring-soon'] : ''}`}>
          <div className={styles['expiry-icon']}>
            <Calendar size={20} color="#EA582C" />
          </div>
          <div className={styles['expiry-info']}>
            <span className={styles['expiry-label']}>
              {isExpired(consentExpiry) ? 'Consent Expired' : 'Consent Valid Until'}
            </span>
            <strong className={styles['expiry-date']}>
              {new Date(consentExpiry).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }).replace(/\//g, '-')}
            </strong>
          </div>
          {!isExpired(consentExpiry) && (() => {
            const days = Math.ceil((new Date(consentExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            return days > 0 ? <span className={styles['expiry-days-badge']}>+ {days} Days</span> : null;
          })()}
        </div>
      )}

      {/* Rights Preview */}
      <div className={styles['rights-preview']}>
        <h3>{t.consent?.yourRights || 'Your Rights Under DPDP Act 2023'}</h3>
        <ul className={styles['rights-list']}>
          <li>{t.consent?.rightToAccess || 'Right to access your data'}</li>
          <li>{t.consent?.rightToCorrect || 'Right to correct your data'}</li>
          <li>{t.consent?.rightToWithdraw || 'Right to withdraw consent'}</li>
          <li>{t.consent?.rightToGrievance || 'Right to file grievances'}</li>
        </ul>
      </div>

      <div className={styles['action-buttons']}>
        <button className={styles['start-review-btn']} onClick={() => goToStep('documents')}>
          {t.consent?.startReview || 'Start Review'}
        </button>
      </div>
    </div>
  );

  // Render Documents Step - Improved with better UX
  const renderDocuments = () => {
    const remainingCount = allDocuments.length - documentsRead.size;

    return (
      <div className={`${styles['simple-wizard-step']} ${styles['documents-step']}`}>
        <div className={styles['step-header']}>
          <h2>{t.consent?.reviewDocuments || 'Review Documents'}</h2>
          <p>{t.consent?.pleaseReadCarefully || 'Please read the documents carefully before providing your consent.'}</p>
        </div>

        {/* Documents List */}
        <div className={styles['documents-list']} role="list">
          {allDocuments.map((doc, idx) => {
            const docId = `${doc.templateId}-${doc.type}`;
            const isRead = documentsRead.has(docId);
            const isExpanded = expandedDoc === docId;
            
            return (
              <div 
                key={docId} 
                className={`${styles['document-card']} ${isRead ? styles['read'] : ''} ${isExpanded ? styles['expanded'] : ''}`}
                role="article"
                aria-labelledby={`doc-title-${idx}`}
              >
                <button 
                  className={styles['document-header']}
                  onClick={() => {
                    setExpandedDoc(isExpanded ? null : docId);
                  }}
                  aria-expanded={isExpanded}
                  aria-controls={`doc-content-${idx}`}
                  aria-label={`${doc.title}. ${isRead ? 'Already reviewed' : 'Not yet reviewed'}. Click to ${isExpanded ? 'collapse' : 'expand'}.`}
                >
                  <div className={styles['doc-main']}>
                    <span className={styles['doc-title']} id={`doc-title-${idx}`}>{doc.title}</span>
                  </div>
                  <span className={styles['doc-status-badges']}>
                    {isRead ? (
                      <span className={`${styles['status-badge']} ${styles['reviewed']}`}>Reviewed</span>
                    ) : isExpanded ? (
                      <span className={`${styles['status-badge']} ${styles['reading']}`}>Reading</span>
                    ) : (
                      <span className={`${styles['status-badge']} ${styles['pending']}`}>Pending</span>
                    )}
                  </span>
                  <ChevronUp size={16} className={`${styles['doc-chevron']} ${isExpanded ? '' : styles['collapsed']}`} />
                </button>
                
                {isExpanded && (
                  <div className={styles['document-content']}>
                    <ContentRenderer
                      content={doc.content}
                      contentType={doc.contentType}
                      isRead={isRead}
                      onScrolledToEnd={(reached) => {
                        if (reached) {
                          setScrolledDocs(prev => new Set(prev).add(docId));
                        }
                      }}
                    />
                    {/* Content team approved text — do not change without content team approval */}
                    <div className={styles['document-actions']}>
                      <label className={styles['confirm-read-label']}>
                        <input
                          type="checkbox"
                          checked={isRead}
                          onChange={(e) => {
                            if (e.target.checked) {
                              markDocumentRead(docId);
                            } else {
                              unmarkDocumentRead(docId);
                            }
                          }}
                          className={styles['confirm-read-checkbox']}
                        />
                        <span>I confirm I have read / heard and understood the above.</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles['action-buttons']}>
          <Button variant="ghost" onClick={() => goToStep('welcome', -1)}>
            ← Back
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => goToStep('consent')}
            disabled={!allDocsRead}
            aria-label={allDocsRead ? 'Proceed to consent step' : `Please read ${remainingCount} remaining document${remainingCount !== 1 ? 's' : ''} before continuing`}
          >
            {t.common?.next || 'Next'} →
          </Button>
        </div>
      </div>
    );
  };

  // Render Consent Step - Improved with better visual feedback
  const renderConsent = () => {
    // If OTP is selected and not yet verified, show OTP step
    if (selectedEvidence && ['PHONE_OTP', 'EMAIL_OTP', 'SMS_OTP'].includes(selectedEvidence) && !evidenceData?.otpVerified) {
      return (
        <OTPStep
          phoneNumber={formData.dataOwner?.phoneNumber}
          email={formData.dataOwner?.email}
          channel={selectedEvidence === 'EMAIL_OTP' ? 'EMAIL' : 'SMS'}
          onNext={handleOtpComplete}
          onBack={() => {
            // Reset to signature if available
            if (evidenceTypes.includes('SIGNATURE')) {
              setSelectedEvidence('SIGNATURE');
            }
          }}
          tenantId={tenantId}
          formId={urlId}
          token={token}
        />
      );
    }

    // If Video recording is selected and not yet completed, show Video step
    if (selectedEvidence === 'VIDEO' && !evidenceData?.videoUrl) {
      const consentText = formData.templates[0]?.consentData || 
        "I hereby give my consent to the processing of my personal data as described in the documents I have reviewed.";
      
      return (
        <VideoRecordingStep
          consentText={consentText}
          onNext={(videoBlob: Blob, duration: number) => {
            // Create a URL for the video blob
            const videoUrl = URL.createObjectURL(videoBlob);
            setEvidenceData({
              type: 'VIDEO',
              videoUrl: videoUrl,
              videoDuration: duration
            });
          }}
          onBack={() => {
            if (evidenceTypes.includes('SIGNATURE')) {
              setSelectedEvidence('SIGNATURE');
            }
          }}
          minimumDuration={10}
        />
      );
    }

    // If Audio recording is selected and not yet completed, show Audio step
    if (selectedEvidence === 'AUDIO' && !evidenceData?.audioUrl) {
      const consentText = formData.templates[0]?.consentData || 
        "I hereby give my consent to the processing of my personal data as described in the documents I have reviewed.";
      
      return (
        <AudioRecordingStep
          consentText={consentText}
          onNext={(audioBlob: Blob, duration: number) => {
            const audioUrl = URL.createObjectURL(audioBlob);
            setEvidenceData({
              type: 'AUDIO',
              audioUrl: audioUrl,
              audioDuration: duration
            });
          }}
          onBack={() => {
            if (evidenceTypes.includes('SIGNATURE')) {
              setSelectedEvidence('SIGNATURE');
            }
          }}
          minimumDuration={5}
        />
      );
    }

    const evidenceInfo = getEvidenceTypeName(selectedEvidence || 'SIGNATURE');

    return (
      <div className={`${styles['simple-wizard-step']} ${styles['consent-step']}`}>
        <div className={styles['step-header']}>
          <p>Review and confirm your consent below</p>
        </div>

        {/* Consent Summary Card */}
        <Card className={styles['consent-summary-card']}>
          <div className={styles['summary-header']}>
            <h3>Consent Summary</h3>
            <span className={`${styles['status-badge']} ${styles['reviewed']}`}>Doc Reviewed</span>
          </div>

          <div className={styles['summary-content']}>
            {/* Organizations */}
            {organizations.length > 0 && (
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Consenting To</span>
                <span className={styles['summary-value']}>
                  {organizations.map(o => o.name).join(', ')}
                </span>
              </div>
            )}

            {/* Data Owner */}
            {formData.dataOwner && (
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Consenting Given By</span>
                <span className={styles['summary-value']}>
                  {formData.dataOwner.firstName} {formData.dataOwner.lastName}
                </span>
              </div>
            )}

            {/* Data Being Collected */}
            {dataCategories.length > 0 && (
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>{t.consent?.dataBeingCollected || 'Data Being Collected'}</span>
                <span className={styles['summary-value']}>
                  {dataCategories.slice(0, 3).join(', ')}
                  {dataCategories.length > 3 && ` +${dataCategories.length - 3} more`}
                </span>
              </div>
            )}

            {/* Confirmation Method */}
            <div className={styles['summary-row']}>
              <span className={styles['summary-label']}>Confirmation Method</span>
              <span className={styles['summary-value']}>
                {evidenceInfo.icon} {evidenceInfo.name}
              </span>
            </div>
          </div>
        </Card>

        {/* Evidence Type Selection - Only show if multiple options available */}
        {evidenceTypes.length > 1 && (
          <div className={styles['evidence-selection']}>
            <div className={styles['evidence-options']}>
              {evidenceTypes.map((type, idx) => {
                const info = getEvidenceTypeName(type);
                const isSelected = selectedEvidence === type ||
                  (type === 'PHONE_OTP' && selectedEvidence === 'SMS_OTP') ||
                  (type === 'SMS_OTP' && selectedEvidence === 'PHONE_OTP');

                return (
                  <React.Fragment key={type}>
                    {idx > 0 && (
                      <div className={styles['or-divider']}>
                        <span className={styles['or-line']}></span>
                        <span className={styles['or-text']}>or</span>
                        <span className={styles['or-line']}></span>
                      </div>
                    )}
                    <button
                      className={`${styles['evidence-option']} ${styles['evidence-alt-btn']} ${isSelected ? styles['selected'] : ''}`}
                      onClick={() => {
                        setSelectedEvidence(type);
                        // Clear previous evidence data when switching types
                        setEvidenceData(null);
                      }}
                    >
                      <span className={styles['evidence-icon']}>{info.icon}</span>
                      <span className={styles['evidence-name']}>{info.name}</span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Signature Pad */}
        {selectedEvidence === 'SIGNATURE' && (
          <Card className={styles['signature-card']}>
            <div className={styles['signature-header']}>
              <span className={styles['signature-icon']}><PenLine size={20} /></span>
              <div>
                <h3>{t.consent?.digitalSignature || 'E-Signature based consent'}</h3>
                <p>{t.consent?.drawSignature || 'Draw your signature'}</p>
              </div>
            </div>
            <div className={styles['signature-area']}>
              <SignaturePad
                value={signature}
                onSignatureChange={setSignature}
              />
            </div>
            {signature && (
              <div className={styles['signature-status']}>
                <span className={styles['status-success']}><CheckCircle size={16} className={styles['inline-icon']} /> Signature captured</span>
              </div>
            )}
          </Card>
        )}

        {/* Checkbox Consent */}
        {selectedEvidence === 'CHECKBOX' && (
          <Card className={styles['checkbox-consent-card']}>
            <div className={styles['checkbox-icon']}><CheckSquare size={20} /></div>
            <p>Your consent will be recorded when you check the box below and submit.</p>
          </Card>
        )}

        {/* OTP Verified Badge */}
        {evidenceData?.otpVerified && (
          <Card className={styles['otp-verified-card']}>
            <div className={styles['verified-badge']}>
              <span className={styles['verified-icon']}><CheckCircle size={20} /></span>
              <span className={styles['verified-text']}>{t.consent?.otpVerified || 'Phone Verified Successfully'}</span>
            </div>
            <p className={styles['verified-number']}>
              {formatPhoneNumber(formData.dataOwner?.phoneNumber)}
            </p>
          </Card>
        )}

        {/* Video Recording Completed Badge */}
        {evidenceData?.videoUrl && (
          <Card className={styles['video-verified-card']}>
            <div className={styles['verified-badge']}>
              <span className={styles['verified-icon']}><Video size={20} /></span>
              <span className={styles['verified-text']}>{t.consent?.recordingComplete || 'Video Recorded Successfully'}</span>
            </div>
            <p className={styles['verified-details']}>
              Duration: {evidenceData.videoDuration}s
            </p>
            <button
              className={styles['retake-btn']}
              onClick={() => setEvidenceData(null)}
            >
              <RefreshCw size={16} className={styles['inline-icon']} /> {t.consent?.retake || 'Retake'} Video
            </button>
          </Card>
        )}

        {/* Audio Recording Completed Badge */}
        {evidenceData?.audioUrl && (
          <Card className={styles['audio-verified-card']}>
            <div className={styles['verified-badge']}>
              <span className={styles['verified-icon']}><Mic size={20} /></span>
              <span className={styles['verified-text']}>{t.consent?.recordingComplete || 'Audio Recorded Successfully'}</span>
            </div>
            <p className={styles['verified-details']}>
              Duration: {evidenceData.audioDuration}s
            </p>
            <button
              className={styles['retake-btn']}
              onClick={() => setEvidenceData(null)}
            >
              <RefreshCw size={16} className={styles['inline-icon']} /> {t.consent?.retake || 'Retake'} Audio
            </button>
          </Card>
        )}

        {/* Final Consent Checkbox */}
        <Card className={`${styles['final-consent-card']} ${acceptedTerms ? styles['accepted'] : ''}`}>
          <label className={styles['consent-checkbox-label']}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAcceptedTerms(e.target.checked)}
              className={styles['consent-checkbox']}
            />
            <div className={styles['consent-text']}>
              {t.consent?.readAllDocuments || 'I understand that my consent is voluntary and can be withdrawn at any time'}
            </div>
          </label>
        </Card>

        {/* Error Message */}
        {error && (
          <div className={styles['error-message']}>
            <span className={styles['error-icon']}><AlertTriangle size={20} /></span>
            <span>{error}</span>
          </div>
        )}

        <div className={styles['action-buttons']}>
          <Button variant="ghost" onClick={() => goToStep('documents', -1)} disabled={isSubmitting}>
            ← {t.common?.back || 'Back'}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            isLoading={isSubmitting}
          >
            {isSubmitting ? t.consent?.submitting || 'Submitting...' : <>{t.common?.submit || 'Submit'} <Check size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /></>}
          </Button>
        </div>
      </div>
    );
  };

  // Render Success Step - Improved with better visual feedback
  const renderSuccess = () => {
    return (
      <div className={`${styles['simple-wizard-step']} ${styles['success-step']}`}>
        <h2 className={styles['success-title']}>{t.consent?.consentSubmitted || 'Consent Recorded Successfully'}</h2>
        <p className={styles['success-message']}>
          {t.consent?.thankYou || 'Your Consent has been securely recorded and will be processed according to the terms you agreed to.'}
        </p>
      </div>
    );
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 'welcome': return renderWelcome();
      case 'documents': return renderDocuments();
      case 'consent': return renderConsent();
      case 'success': return renderSuccess();
      default: return renderWelcome();
    }
  };

  const stepOrder: SimpleStep[] = ['welcome', 'documents', 'consent', 'success'];
  const currentStepIndex = stepOrder.indexOf(currentStep);
  const progress = currentStep === 'success' ? 100 : (currentStepIndex / (stepOrder.length - 1)) * 100;

  // Announce step changes for screen readers
  const getStepAnnouncement = () => {
    switch (currentStep) {
      case 'welcome': return t.consent?.yourConsentRequired || 'Your consent is required';
      case 'documents': return `${t.consent?.reviewDocuments || 'Review Documents'}: ${allDocuments.length} ${t.consent?.documentsToRead || 'documents to read'}`;
      case 'consent': return t.consent?.confirmConsent || 'Confirm your consent';
      case 'success': return t.consent?.consentSubmitted || 'Consent submitted successfully';
      default: return '';
    }
  };

  return (
    <div 
      className={styles['simple-consent-wizard']}
      role="main"
      aria-label={t.a11y?.mainContent || 'Consent wizard'}
    >
      {/* Skip to main content link for keyboard users */}
      <a href="#wizard-content" className={styles['skip-link']}>
        Skip to main content
      </a>

      {/* Language Selector */}
      <div className={styles['wizard-header-controls']}>
        <LanguageSelector compact ariaLabel={t.a11y?.navigation || 'Select language'} />
      </div>

      {currentStep !== 'success' && currentStep !== 'welcome' && (
        <div 
          className={styles['wizard-progress']}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t.a11y?.progressIndicator?.replace('{current}', String(currentStepIndex + 1)).replace('{total}', '3') || `Progress: Step ${currentStepIndex + 1} of 3`}
        >
          <div className={styles['step-trail']} role="tablist" aria-label="Wizard steps">
            {['Review Documents', 'Consent Confirmation'].map((label, index) => {
              const trailIndex = currentStep === 'documents' ? 0 : currentStep === 'consent' ? 1 : 1;
              const isCompleted = index < trailIndex;
              const isActive = index === trailIndex;
              return (
              <div key={index} className={`${styles['step-trail-item']} ${isCompleted ? styles['completed'] : ''} ${isActive ? styles['active'] : ''}`}>
                <div className={styles['step-trail-circle']}>
                  {isCompleted ? <Check size={14} /> : <span>{index + 1}</span>}
                </div>
                <div className={styles['step-trail-content']}>
                  <span className={styles['step-trail-label']} role="tab" aria-selected={isActive}>
                    {index === 0 ? (t.consent?.reviewDocuments || 'Review Documents') : (t.consent?.provideConsent || 'Consent Confirmation')}
                  </span>
                </div>
                {index < 1 && <div className={styles['step-trail-connector']} />}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Screen reader announcement */}
      <div className={styles['sr-only']} role="status" aria-live="polite" aria-atomic="true">
        {getStepAnnouncement()}
      </div>

      <div id="wizard-content" tabIndex={-1}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ opacity: 0, x: direction > 0 ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction < 0 ? 30 : -30 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            role="tabpanel"
            aria-label={getStepAnnouncement()}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SimpleConsentWizard;
